import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { me, logout } from '../api/auth';
import { ApiClientError } from '../api/client';
import { useKeeper } from '../api/keeper';
import { useBadges } from '../api/badges';
import { useCollections } from '../api/collections';
import { useCompanions } from '../api/companions';
import { useJournal } from '../api/journal';
import { useNotificationPrefs } from '../api/notifications';
import { useInvites } from '../api/coop';
import { useChallenges } from '../api/arena';
import { useFriends } from '../api/friends';
import { useSurfaces } from '../api/surfaces';
import { keeperBodyPath } from '../compositor/assetPaths';
import { PetFace } from '../pet3d/PetFace';
import { SlotsRow } from '../habitat/SlotsRow';
import { resetAchievementBaselines } from '../shell/useAchievementCelebrations';
import { resetLevelBaseline } from '../shell/levelBaseline';
import { resetDailyReceipts } from '../shell/useDailyReceipts';
import { ScreenBackdrop } from '../shell/ScreenBackdrop';
import { Studio } from '../shell/Studio';
import { useLayoutTier, isStudio } from '../shell/layoutTiers';
import { UiSticker } from '../shell/UiSticker';
/**
 * One destination in a section's tile grid. Every Me-hub tile carries a raster `ui_icon_*`
 * sticker (SCREENS.md "/me — Me (layout)" tile icons). `count` (P2, 2026-09-14) is the one number
 * this screen's own standing rule allows: real progress from a query the screen already makes
 * (Badges/Collections), never an invented figure — see that rule on `MeScreen` below.
 */
function MeTile({ to, label, sticker, count }: { to: string; label: string; sticker?: string; count?: string }) {
  return (
    <Link to={to} className="me-tile">
      {sticker && <UiSticker id={sticker} className="me-tile-sticker" />}
      <span className="me-tile-label">{label}</span>
      {count && <span className="me-tile-count">{count}</span>}
    </Link>
  );
}

/**
 * The desk's right panel (P2, 2026-09-14, Studio Task 11): this app has no notification FEED
 * endpoint — `useNotificationPrefs()` is a settings query, not an inbox — so "Inbox" in the demo
 * this branch's desk work is approving against is scoped down to what the server actually serves:
 * the account's notification CATEGORIES, as chips, each linking to the real settings screen where
 * they can be acted on. Recorded in RULINGS per the task brief's own instruction.
 */
function NotificationCategoryChips() {
  const prefs = useNotificationPrefs();
  return (
    <div className="me-notif-chips">
      <h2 className="me-notif-chips-title">Notifications</h2>
      {prefs.data ? (
        <ul className="me-notif-chips-list" role="list">
          {prefs.data.categories.map((c) => (
            <li key={c.code}>
              <Link to="/me/notifications" className="chip">{c.label}</Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="skeleton skeleton-chip" />
      )}
    </div>
  );
}

export function MeScreen() {
  const nav = useNavigate();
  const location = useLocation();
  const q = useQuery({ queryKey: ['me'], queryFn: me, retry: false });
  const keeperQuery = useKeeper();
  const queryClient = useQueryClient();
  const tier = useLayoutTier();
  const studio = isStudio(tier);
  const three = tier === 'studio3';
  // Declared BEFORE badges/collections/companions below, and referenced by their own `enabled` —
  // see the fix-round-2 note on those three lines for why the order and the gate both matter.
  const logoutM = useMutation({
    mutationFn: logout,
    // Two resets on logout, both closing the same class of bug: `useAchievementCelebrations`'s
    // module-level baselines are deliberately process-lifetime (they must survive an AppShell
    // unmount for a battle), which means nothing ever re-seeds them — so without this, logging
    // out of account A and into account B in the same tab diffs B's already-awarded badges
    // against A's baseline and fires a celebration per badge B holds that A didn't. And a react
    // query cache left populated across accounts is its own, separate stale-data problem, so it
    // is cleared here too rather than left to whatever screen happens to refetch first. The level
    // baseline (shell/levelBaseline) and the daily-receipt map (shell/useDailyReceipts) are the
    // same class: module state keyed by companion id / daily code, and `dailyResetAt` is the same
    // midnight for every account — so B would be diffed against A (a false "Daily done").
    onSuccess: () => {
      resetAchievementBaselines();
      resetLevelBaseline();
      resetDailyReceipts();
      queryClient.clear();
      nav('/login');
    },
  });
  // P2 (2026-09-14): this screen's own standing rule, restated — a count is added ONLY when it is
  // real progress from a query the screen already makes for its own navigation rows, never
  // invented for decoration (the hero comment above already refuses to fabricate level/Shells/
  // streak for exactly this reason). Badges and Collections are the only two rows that get one;
  // Friends/Chat/Guild/Wishlist stay bare rather than growing a fan-out of new queries whose only
  // job would be a number on a tile.
  //
  // Fix round 2 (review): `enabled: !logoutM.isPending && !logoutM.isSuccess` disarms all three
  // observers for the ENTIRE logout request, not just after it resolves. `queryClient.clear()`
  // above wipes every query, but this component is still mounted at that exact synchronous
  // instant — `nav('/login')` only SCHEDULES the router's state update; React does not unmount
  // `MeScreen` until it actually re-renders, which happens after `onSuccess` returns. Reordering
  // `nav()` to run first does not change that window (verified: still flaky red 5/5 runs) — a
  // still-mounted `useQuery` observer whose query was just removed refetches immediately by
  // design, which is exactly what repopulated `badgesKeys.all` before account B ever logged in.
  // `isPending` goes true the instant `.mutate()` is called (before this request even lands), so
  // gating on it (not just `isSuccess`) closes the window completely rather than narrowing it —
  // no fetch these three hooks could start is ever in flight for `clear()` to race against.
  const badges = useBadges(!logoutM.isPending && !logoutM.isSuccess);
  const collections = useCollections(!logoutM.isPending && !logoutM.isSuccess);
  const companions = useCompanions(!logoutM.isPending && !logoutM.isSuccess);
  const journal = useJournal(!logoutM.isPending && !logoutM.isSuccess);
  const journalCount = journal.data
    ? `Today ${journal.data.dailies.filter((d) => d.done).length} of ${journal.data.dailies.length}` : undefined;
  // QA 2026-09-23 (social F26): a pending co-op invite or duel challenge lived only inside
  // Me -> Notifications, so an invitee never learned one was waiting. The two inbox sources this
  // app has (there is no notification feed endpoint) put a count on the tile that leads there.
  // Same logout gate as the three queries above, for the same cache-repopulation race.
  const invites = useInvites({ enabled: !logoutM.isPending && !logoutM.isSuccess });
  const challenges = useChallenges({ enabled: !logoutM.isPending && !logoutM.isSuccess });
  // Cut 1 (2026-09-27, "Automatic, sellers exempt"): the gallery and Guild tiles show once they
  // have content, Wishlist once there is a friend to gift from it. Same logout gate as above.
  const surfaces = useSurfaces(!logoutM.isPending && !logoutM.isSuccess);
  const hasFriends = (useFriends(!logoutM.isPending && !logoutM.isSuccess).data?.length ?? 0) > 0;

  // KeeperCreatorScreen navigates back here with `state: { toast }` on a successful save
  // (SCREENS.md "/me/keeper" saved state: "Toast 'Keeper saved'; return to /me") — same
  // location-state convention LoginScreen already uses for its own "session-expired" note.
  const [toast, setToast] = useState<string | null>((location.state as { toast?: string } | null)?.toast ?? null);

  const sessionExpired = q.error instanceof ApiClientError && q.error.status === 401;
  useEffect(() => {
    if (sessionExpired) nav('/login', { state: { reason: 'session-expired' } });
  }, [sessionExpired, nav]);

  if (q.isLoading) {
    return (
      <main className="screen-form me">
        <ScreenBackdrop id="bd_me_default" />
        <div className="skeleton skeleton-line" />
        <div className="skeleton skeleton-chip" />
      </main>
    );
  }

  if (sessionExpired) return null; // redirecting

  if (q.isError) {
    return (
      <main className="screen-form me">
        <ScreenBackdrop id="bd_me_default" />
        <div className="retry-card">
          <p>Couldn&rsquo;t reach Project Pets.</p>
          <button className="btn-primary" onClick={() => q.refetch()}>Try again</button>
        </div>
      </main>
    );
  }

  const account = q.data!;
  const keeper = keeperQuery.data;
  // Hero fallback (P2): no Keeper yet is the common case for a fresh account, and until now that
  // meant an empty bordered square where the player's own screen should show THEM. The account's
  // active companion (falling back to its first, same "pick something real" shape `defaultActiveGroup`
  // in the wardrobe already uses) is exactly as real as the Keeper portrait it stands in for.
  // `useCompanions()` above is fetched for exactly this — this screen shows no companion count.
  const activeCompanion = companions.data?.find((c) => c.isActive) ?? companions.data?.[0];
  const badgesCount = badges.data
    ? `${badges.data.badges.filter((b) => b.awarded).length} of ${badges.data.badges.length}`
    : undefined;
  const collectionsCount = collections.data
    ? `${collections.data.sets.filter((s) => s.completed).length} of ${collections.data.sets.length}`
    : undefined;
  const waiting = (invites.data?.length ?? 0) + (challenges.data?.incoming.length ?? 0);
  const inboxCount = waiting > 0 ? `${waiting} waiting` : undefined;

  // THE HERO. This screen used to open with a bare <h1> and an age-band chip over a stack of
  // identical --btn-min-target bars — it showed the player nothing about themselves, which is why
  // as a settings menu wearing a game's palette. The Keeper portrait and handle are both
  // already fetched here; nothing new is queried for it.
  // Deliberately NOT shown: level, Shells, streak. Level is a COMPANION property and an
  // account can hold six of them, so an account-level "Lv 7" would be wrong rather than
  // merely approximate; Shells and the streak live on queries this screen does not make, and
  // a fan-out of three more for decoration is a real cost. The mockup showed all three; they
  // were invented, and inventing them is what ItemDrawer's own "no acquiredAt, so no newest
  // sort" note refuses to do.
  const hero = (
    <div className="me-hero">
      {keeper ? (
        <img src={keeperBodyPath(keeper.bodyPreset, keeper.skinTone)} alt="" className="me-hero-avatar" />
      ) : activeCompanion ? (
        <PetFace species={activeCompanion.speciesCode} stage={activeCompanion.stage} outfit={activeCompanion.outfit} alt={activeCompanion.name} className="me-hero-avatar" />
      ) : (
        <span className="me-hero-avatar me-hero-avatar-empty" aria-hidden="true" />
      )}
      <div className="me-hero-text">
        <h1 title={account.displayName}>{account.displayName}</h1>
        {/* onboard F18: player-facing wording, never the enum; UNKNOWN has nothing to say. */}
        {account.ageBand === 'ADULT' && <span className="chip">Adult account</span>}
        {account.ageBand === 'MINOR' && <span className="chip">Under 18</span>}
      </div>
    </div>
  );
  // SCREENS.md "/habitat — equipment additions (Phase 4)" "companion-slots-row" moved here
  // from the Habitat identity bar. Slot state is account-wide, not per-companion
  // (SlotsRow.tsx's own doc comment), so it always read as account bookkeeping sitting in
  // one specific pet's den — and it was the single largest consumer of that bar's height.
  // "Your Project Pets" is already the account-level group. Behaviour is unchanged: same
  // component, same purchase CTA for slots 4–6. Built once (WardrobeScreen/HabitatScreen's own
  // "one element, one place per tier" shape) so the desk's left panel and the phone's inline row
  // are never two competing instances of the same query + mutation.
  const companionsBlock = <SlotsRow />;

  return (
    <Studio
      // `studio-flow`: Me scrolls in `.content-region` (it owns no scroller of its own), so it
      // opts out of the Studio's fixed height chain — see base.css "A DOCUMENT-FLOW screen".
      className="me-studio studio-flow"
      leftLabel="Profile"
      rightLabel="Notifications"
      left={three ? <div className="me-side">{hero}{companionsBlock}</div> : undefined}
      right={studio ? <NotificationCategoryChips /> : undefined}
    >
      <main className="screen-form me">
        <ScreenBackdrop id="bd_me_default" />
        {!three && hero}

        {toast && (
          <div className="toast" role="alert">
            <span>{toast}</span>
            <button type="button" className="toast-dismiss" onClick={() => setToast(null)}>Dismiss</button>
          </div>
        )}

        {/* Task 15 (Phase 6): the first section-header pattern on this screen — every pre-existing
            row (unchanged individually) now sits under a labelled group instead of one flat list,
            making room for the new Social section without the page reading as an undifferentiated
            wall of rows. Each section is its own `<nav>` (a distinct landmark per group, not one
            `<nav>` with `<h2>`s stitched in) so a screen-reader's landmark list names each group.
            P2 (2026-09-14): each section is also a `.me-group` sticker — its own bordered, shadowed
            card, matching every other multi-item surface's "second surface inside a sticker"
            treatment (the Screen layout overhaul's `--c-well` rule), instead of four headings
            floating over the same flat backdrop the hero and the toast also sit on. */}
        <section className="me-nav-section me-group" aria-labelledby="me-nav-projectpets">
          <h2 id="me-nav-projectpets" className="me-nav-section-title">Your Project Pets</h2>
          <nav className="me-tiles" aria-labelledby="me-nav-projectpets">
            <MeTile to="/me/keeper" label={keeper ? 'Your Keeper' : 'Create your Keeper'} sticker="ui_icon_keeper" />
            {/* encodeURIComponent: display names can contain reserved URL chars (see the identical
                fix + comment in LookGalleryScreen's owner link) — the fetch side already encodes. */}
            <MeTile to={`/profile/${encodeURIComponent(account.displayName)}`} label="Your profile" sticker="ui_icon_profile" />
            {/* SCREENS.md doesn't name a distinct entry point for the Look gallery anywhere else
                (Habitat, the Looks sheet) — only that publishing "appears in the public gallery".
                Without a link here /looks/gallery would be unreachable from the UI at all, so Me
                (already the hub for Keeper/profile navigation) is the natural fallback location. */}
            {surfaces.gallery && <MeTile to="/looks/gallery" label="Look gallery" sticker="ui_icon_gallery" />}
          </nav>
          {!three && companionsBlock}
        </section>

        <section className="me-nav-section me-group" aria-labelledby="me-nav-progress">
          <h2 id="me-nav-progress" className="me-nav-section-title">Progress</h2>
          <nav className="me-tiles" aria-labelledby="me-nav-progress">
            {/* Task 22 (§8.7 "visible progress everywhere"): Badges/Collections. */}
            <MeTile to="/me/badges" label="Badges" sticker="ui_icon_badges" count={badgesCount} />
            <MeTile to="/me/collections" label="Collections" sticker="ui_icon_collections" count={collectionsCount} />
            {/* onboard F25 / F9: on the phone the day's tasks lived only at /journal, which no
                home screen linked to (the desk has the Dashboard's Today card). Same real-count
                rule as the two tiles above — the journal query, never an invented number. */}
            <MeTile to="/journal" label="Journal" sticker="ui_icon_map_pin" count={journalCount} />
          </nav>
        </section>

        {/* Phase 6 Task 15 brief: "Social: Friends/Chat/Guild/Wishlist" — the new section this task
            adds. These four routes are registered by Tasks 16-19 (the screens themselves don't
            exist yet on this branch); the links are wired now because this task's own scope is the
            Me-hub navigation shape, per the brief's explicit task split. */}
        <section className="me-nav-section me-group" aria-labelledby="me-nav-social">
          <h2 id="me-nav-social" className="me-nav-section-title">Social</h2>
          <nav className="me-tiles" aria-labelledby="me-nav-social">
            <MeTile to="/me/friends" label="Friends" sticker="ui_icon_friends" />
            <MeTile to="/me/chat" label="Chat" sticker="ui_icon_chat" />
            {surfaces.guilds && <MeTile to="/guild" label="Guild" sticker="ui_icon_guild" />}
            {hasFriends && <MeTile to="/me/wishlist" label="Wishlist" sticker="ui_icon_wishlist" />}
          </nav>
        </section>

        <section className="me-nav-section me-group" aria-labelledby="me-nav-settings">
          <h2 id="me-nav-settings" className="me-nav-section-title">Settings</h2>
          <nav className="me-tiles" aria-labelledby="me-nav-settings">
            {/* Task 23 (SCREENS.md "/me/notifications"). */}
            <MeTile to="/me/notifications" label="Notifications" sticker="ui_icon_notifications" count={inboxCount} />
          </nav>
        </section>

        {/* QA 2026-09-23 (social F26): a staff account had no way to the console but typing
            `/admin`. `role` is on the account view only for staff (`AccountView.role`). */}
        {account.role === 'staff' && (
          <section className="me-nav-section me-group" aria-labelledby="me-nav-staff">
            <h2 id="me-nav-staff" className="me-nav-section-title">Staff</h2>
            <nav className="me-tiles" aria-labelledby="me-nav-staff">
              <MeTile to="/admin" label="Staff console" sticker="ui_icon_report" />
            </nav>
          </section>
        )}

        <div className="actions">
          <button
            className="btn-destructive-quiet"
            disabled={logoutM.isPending}
            onClick={() => logoutM.mutate()}
          >
            {logoutM.isPending ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      </main>
    </Studio>
  );
}
